'use client';

import { c as _c } from "react/compiler-runtime";
import { experienceFormSubject } from '@data-access/lib/gql/data/experience-form-subject';
import type { NexperienceQuery } from '@data-access/lib/gql/experience/documents';
import { CartCurrencyMismatchBox, CartCurrencyProvider } from '@feat/feat-cart/currency';
import { useExperienceData } from '@feat/feat-experience/data';
import { trackViewItem } from '@feat/feat-tracking/view';
import { UrlStateProvider } from '@feat/feat-url-state/provider';
import type { UrlState } from '@feat/feat-url-state/schema';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { type DehydratedState, HydrationBoundary } from '@tanstack/react-query';
import { NON_CANCELABLE } from '@utils/utils-config';
import dynamic from 'next/dynamic';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { PreviewBadge } from 'src/app/_page-chrome/preview-badge';
import { AppExperienceBookingBox } from 'src/app/erlebnisse/_components/app-experience-booking-box';
import { AppExperienceCancelation } from 'src/app/erlebnisse/_components/app-experience-cancelation';
import { AppExperienceSuggestionsLoading, AppWhoWeAreLoading } from 'src/app/erlebnisse/_components/app-experience-deferred-section-loading';
import { ExperienceFilterProvider } from 'src/app/erlebnisse/_components/app-experience-filter-provider';
import { AppExperienceOpeningHoursSection } from 'src/app/erlebnisse/_components/app-experience-opening-hours-section';
import { AppExperienceStructuredData } from 'src/app/erlebnisse/_components/app-experience-structured-data';
import { AppExperienceTicketSection } from 'src/app/erlebnisse/_components/app-experience-ticket-section';
import { AppExperienceTimeslots } from 'src/app/erlebnisse/_components/app-experience-timeslots';
import { EdpDateSelectionArmProvider } from 'src/app/erlebnisse/_components/edp-date-selection-arm';
import { useAppExperienceReviews } from 'src/app/erlebnisse/_hooks/use-app-experience-reviews';
import type { ExperienceFilter } from 'src/app/erlebnisse/_lib/experience-state-types';
import type { TicketCalendarPlan } from 'src/app/erlebnisse/_lib/ticket-calendar';
import type { ProductReviewSummary } from 'src/lib/trusted-shops/product-reviews';
import { HeroSection } from 'src/shared/composites/product-hero';
import { Separator } from 'src/shared/composites/separator';
import { AppExperienceBreadcrumbs } from './app-experience-breadcrumbs';
import { AppExperienceContactUs } from './app-experience-contact-us';
import { AppExperienceGoodToKnow } from './app-experience-good-to-know';
import { AppExperienceHighlights } from './app-experience-highlights';
import { AppExperienceMeetingPoint } from './app-experience-meeting-point';
import { AppExperienceMerchantProfile } from './app-experience-merchant-profile';
import { AppExperiencePrices } from './app-experience-prices';
import { AppExperienceTitle } from './app-experience-title';
import { ExperienceReviews } from './experience-reviews';
const ExperienceSuggestions = dynamic(() => import('./app-experience-suggestions').then(mod => mod.ExperienceSuggestions), {
  loading: () => <AppExperienceSuggestionsLoading />
});
const WhoWeAre = dynamic(() => import('@feat/feat-marketing').then(mod => mod.WhoWeAre), {
  loading: () => <AppWhoWeAreLoading />
});
type ExperienceDetail = NonNullable<NexperienceQuery['nexperience']>;
type ExperienceDetailPageClientProps = {
  experience: ExperienceDetail;
  initialFilter: ExperienceFilter;
  initialUrlState: UrlState;
  dehydratedState: DehydratedState;
  ticketCalendar: TicketCalendarPlan;
  cartCurrency: string | null;
  reviewSummary: ProductReviewSummary | null;
};
function Section(t0) {
  const $ = _c(3);
  const {
    children,
    hidden,
    className
  } = t0;
  if (!children || hidden) {
    return null;
  }
  let t1;
  if ($[0] !== children || $[1] !== className) {
    t1 = <section className={className}>{children}</section>;
    $[0] = children;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function ExperienceDetailPageClient(t0) {
  const $ = _c(17);
  const {
    experience,
    initialFilter,
    initialUrlState,
    dehydratedState,
    ticketCalendar,
    cartCurrency,
    reviewSummary
  } = t0;
  let t1;
  if ($[0] !== experience || $[1] !== reviewSummary) {
    t1 = <EdpDateSelectionArmProvider><ExperienceDetailContent experience={experience} reviewSummary={reviewSummary} /></EdpDateSelectionArmProvider>;
    $[0] = experience;
    $[1] = reviewSummary;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== cartCurrency || $[4] !== t1) {
    t2 = <CartCurrencyProvider cartCurrency={cartCurrency}>{t1}</CartCurrencyProvider>;
    $[3] = cartCurrency;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== experience || $[7] !== initialFilter || $[8] !== t2 || $[9] !== ticketCalendar) {
    t3 = <ExperienceFilterProvider key={experience.id} experience={experience} initialFilter={initialFilter} ticketCalendar={ticketCalendar} urlStateEnabled={true}>{t2}</ExperienceFilterProvider>;
    $[6] = experience;
    $[7] = initialFilter;
    $[8] = t2;
    $[9] = ticketCalendar;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== dehydratedState || $[12] !== t3) {
    t4 = <HydrationBoundary state={dehydratedState}>{t3}</HydrationBoundary>;
    $[11] = dehydratedState;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== initialUrlState || $[15] !== t4) {
    t5 = <UrlStateProvider defaultValue={initialUrlState} initialUrlSync={true}>{t4}</UrlStateProvider>;
    $[14] = initialUrlState;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function ExperienceDetailContent(t0) {
  const $ = _c(97);
  const {
    experience,
    reviewSummary
  } = t0;
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);
  const {
    destination
  } = experience;
  const {
    duration,
    price,
    questionnaireURL
  } = useExperienceData(experience);
  const {
    data: reviews,
    isPending: isReviewStatusPending
  } = useAppExperienceReviews(experience.publicId);
  let t1;
  if ($[0] !== experience.activities || $[1] !== experience.location.lat || $[2] !== experience.location.lng || $[3] !== experience.publicId || $[4] !== experience.title || $[5] !== isReviewStatusPending || $[6] !== reviews?.count) {
    t1 = () => {
      if (isReviewStatusPending) {
        return;
      }
      const reviewCount = reviews?.count ?? 0;
      trackViewItem({
        item_id: `E-${experience.publicId}`,
        item_name: experience.title,
        item_category: "experience",
        item_category2: experience.activities?.join(",") ?? "",
        item_category5: `${experience.location.lat},${experience.location.lng}`,
        item_has_reviews: reviewCount > 0 ? "yes" : "no",
        item_review_count: reviewCount
      });
    };
    $[0] = experience.activities;
    $[1] = experience.location.lat;
    $[2] = experience.location.lng;
    $[3] = experience.publicId;
    $[4] = experience.title;
    $[5] = isReviewStatusPending;
    $[6] = reviews?.count;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = reviews?.count;
  let t3;
  if ($[8] !== experience || $[9] !== isReviewStatusPending || $[10] !== t2) {
    t3 = [experience, isReviewStatusPending, t2];
    $[8] = experience;
    $[9] = isReviewStatusPending;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t1, t3);
  let t4;
  if ($[12] !== experience.metaDescription || $[13] !== price || $[14] !== reviewSummary) {
    t4 = <AppExperienceStructuredData reviewSummary={reviewSummary} description={experience.metaDescription} price={price} />;
    $[12] = experience.metaDescription;
    $[13] = price;
    $[14] = reviewSummary;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PreviewBadge containerClassName="pointer-events-none inset-x-0 z-50 hidden justify-center pt-2 sm:fixed sm:top-(--full-header-height) sm:flex" />;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== experience) {
    t6 = <HeroSection images={experience.images} product={experience} />;
    $[17] = experience;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== experience.currency) {
    t7 = <CartCurrencyMismatchBox productType="experience" productCurrency={experience.currency} />;
    $[19] = experience.currency;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsReviewsOpen(true);
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== experience) {
    t9 = <AppExperienceTitle experience={experience} onOpenReviews={t8} />;
    $[22] = experience;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t9) {
    t10 = <Section className="flex flex-col gap-y-2 py-8 first:pt-0 sm:gap-y-4">{t7}{t9}</Section>;
    $[24] = t7;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = !experience.highlights?.length && !experience.descriptionHtml;
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <HighlightsSectionTitle />;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== experience) {
    t13 = <AppExperienceHighlights experience={experience} />;
    $[28] = experience;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t11 || $[31] !== t13) {
    t14 = <Section className="py-8" hidden={t11}>{t12}{t13}</Section>;
    $[30] = t11;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = !experience.cancelationDeadline || experience.cancelationDeadline === NON_CANCELABLE;
  const t16 = experience.experienceType === "ticket";
  let t17;
  if ($[33] !== experience.cancelationDeadline || $[34] !== t16) {
    t17 = <AppExperienceCancelation cancelationHours={experience.cancelationDeadline} isTicket={t16} />;
    $[33] = experience.cancelationDeadline;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t15 || $[37] !== t17) {
    t18 = <Section className="py-8 sm:hidden" hidden={t15}>{t17}</Section>;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== experience.experienceType) {
    t19 = experience.experienceType === "ticket" ? <AppExperienceTicketSection /> : <AppExperienceTimeslots />;
    $[39] = experience.experienceType;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <AppExperiencePrices />;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = !experience.location;
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <SectionTitle>Treffpunkt</SectionTitle>;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== experience) {
    t23 = <AppExperienceMeetingPoint experience={experience} />;
    $[43] = experience;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t21 || $[46] !== t23) {
    t24 = <Section className="py-8" hidden={t21}>{t22}{t23}</Section>;
    $[45] = t21;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <AppExperienceOpeningHoursSection />;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  let t27;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setIsReviewsOpen(true);
    t27 = () => setIsReviewsOpen(false);
    $[49] = t26;
    $[50] = t27;
  } else {
    t26 = $[49];
    t27 = $[50];
  }
  let t28;
  if ($[51] !== experience.publicId || $[52] !== isReviewsOpen || $[53] !== reviewSummary) {
    t28 = <ExperienceReviews publicId={experience.publicId} reviewSummary={reviewSummary} isOpen={isReviewsOpen} onOpen={t26} onClose={t27} />;
    $[51] = experience.publicId;
    $[52] = isReviewsOpen;
    $[53] = reviewSummary;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <SectionTitle>Gut zu wissen</SectionTitle>;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== duration || $[57] !== experience) {
    t30 = <Section className="py-8">{t29}<AppExperienceGoodToKnow experience={experience} duration={duration} /></Section>;
    $[56] = duration;
    $[57] = experience;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t10 || $[60] !== t14 || $[61] !== t18 || $[62] !== t19 || $[63] !== t24 || $[64] !== t28 || $[65] !== t30) {
    t31 = <div className="col-span-12 sm:col-span-7"><div className="fluid-container divide-y divide-primary/15 *:last:border-primary/15 *:last:border-b sm:divide-y-0 sm:px-0 sm:*:last:border-none! [&>*:has(+[data-experience-opening-hours])]:border-b-0 [&>*:has(+[data-experience-prices])]:border-b-0">{t10}{t14}{t18}{t19}{t20}{t24}{t25}{t28}{t30}</div></div>;
    $[59] = t10;
    $[60] = t14;
    $[61] = t18;
    $[62] = t19;
    $[63] = t24;
    $[64] = t28;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsReviewsOpen(true);
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== experience.location.city || $[69] !== price) {
    t33 = <div className="sm:col-span-5 sm:col-start-8 md:col-span-4 md:col-start-9"><AppExperienceBookingBox price={price} location={experience.location.city} onOpenReviews={t32} /></div>;
    $[68] = experience.location.city;
    $[69] = price;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== t31 || $[72] !== t33) {
    t34 = <div className="sm:fluid-grid sm:fluid-container mt-6 grid sm:mt-10">{t31}{t33}</div>;
    $[71] = t31;
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== experience.merchant) {
    t35 = Boolean(experience.merchant) && <section className="mb-8 py-8"><AppExperienceMerchantProfile merchant={experience.merchant} /></section>;
    $[74] = experience.merchant;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  const t36 = `${questionnaireURL}&subject=${experienceFormSubject.QUESTION_ABOUT_EXPERIENCE}`;
  let t37;
  if ($[76] !== t36) {
    t37 = <AppExperienceContactUs heading="Fragen zum Erlebnis?" ctaText="Kontaktiere uns" href={t36} />;
    $[76] = t36;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== t35 || $[79] !== t37) {
    t38 = <div className="fluid-container pb-8">{t35}{t37}</div>;
    $[78] = t35;
    $[79] = t37;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  let t39;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Separator className="sm:hidden" />;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== experience) {
    t40 = <ExperienceSuggestions experience={experience} />;
    $[82] = experience;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  let t41;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Separator className="sm:hidden" />;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  let t43;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <section className="py-8"><WhoWeAre /></section>;
    t43 = <Separator className="sm:hidden" />;
    $[85] = t42;
    $[86] = t43;
  } else {
    t42 = $[85];
    t43 = $[86];
  }
  let t44;
  if ($[87] !== destination || $[88] !== experience) {
    t44 = <AppExperienceBreadcrumbs experience={experience} destination={destination} placement="bottom" />;
    $[87] = destination;
    $[88] = experience;
    $[89] = t44;
  } else {
    t44 = $[89];
  }
  let t45;
  if ($[90] !== t34 || $[91] !== t38 || $[92] !== t4 || $[93] !== t40 || $[94] !== t44 || $[95] !== t6) {
    t45 = <>{t4}{t5}{t6}{t34}{t38}{t39}{t40}{t41}{t42}{t43}{t44}</>;
    $[90] = t34;
    $[91] = t38;
    $[92] = t4;
    $[93] = t40;
    $[94] = t44;
    $[95] = t6;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  return t45;
}
function SectionTitle(t0) {
  const $ = _c(3);
  const {
    children,
    className
  } = t0;
  const t1 = className ?? "mb-6 font-heading2 sm:mb-8";
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <h2 className={t1}>{children}</h2>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// A/B test FR7-4551: the variant drops the "Highlights" heading. The
// `ab-edp-highlights-heading` flag is read only inside the rendered section, so
// the experiment's exposure events count views where the heading would appear
// rather than every EDP view.
function HighlightsSectionTitle() {
  const $ = _c(1);
  const hideHeading = useFeatureValue("ab-edp-highlights-heading", "control") === "variant";
  if (hideHeading) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionTitle>Highlights</SectionTitle>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}